/**
 * Total value over time — the Home chart, ported from views/dashboard.ejs.
 *
 * Same ids and classes as the EJS card (`#value-chart-card`, `#value-chart`, `#chart-ranges` with
 * `[data-range]` buttons, `#chart-bench-btn`) because the stylesheet styles them by name and the browser suite
 * drives them by name; a card that looks the same but answers to different names is a different control.
 *
 * Chart.js is loaded from the same CDN URL the EJS page uses, once per page, and the card tolerates it never
 * arriving: if the script does not load, the card still renders its heading and range buttons and simply draws
 * nothing. That matters because the tests (and any offline machine) run without a route to the CDN, and a
 * throw here would take the rest of the page's handlers with it — the EJS script had exactly that bug once.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api';

const CHART_SRC = '/vendor/chart.umd.min.js';

type SeriesPoint = { date: string; value: number };
type Series = { series: SeriesPoint[] };

// `public/pf-range.js` — the range window both front ends share. Declared here because it is a plain script,
// not an import: the EJS page loads the same file and calls the same two functions.
declare global {
  interface Window {
    PFRange: {
      cutoffDay: (days: number, now?: number) => string;
      within: (series: SeriesPoint[], days: number | 'all', now?: number) => SeriesPoint[];
    };
  }
}

const RANGES: [string, string][] = [
  ['30', '1M'],
  ['90', '3M'],
  ['365', '1Y'],
  ['1095', '3Y'],
  ['1826', '5Y'],
  ['3652', '10Y'],
  ['5478', '15Y'],
  ['all', 'ALL'],
];

/** Load Chart.js once; resolve to the global, or to null when the CDN is unreachable. */
let chartScript: Promise<unknown> | null = null;
function loadChart(): Promise<unknown> {
  const w = window as unknown as { Chart?: unknown };
  if (w.Chart) return Promise.resolve(w.Chart);
  if (!chartScript) {
    chartScript = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = CHART_SRC;
      s.async = true;
      s.onload = () => resolve((window as unknown as { Chart?: unknown }).Chart || null);
      // A dead CDN is not an error the page should report: the rest of Home is still useful.
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return chartScript;
}

function chartColors() {
  const read = (n: string, fb: string) => (getComputedStyle(document.documentElement).getPropertyValue(n).trim() || fb);
  const accent = read('--accent', '#4f6ef7');
  const theme = document.documentElement.dataset.theme || 'light';
  const m = accent.match(/^#([0-9a-f]{6})$/i);
  const rgb = m ? [parseInt(m[1].slice(0, 2), 16), parseInt(m[1].slice(2, 4), 16), parseInt(m[1].slice(4, 6), 16)] : [79, 110, 247];
  const lightGrid = theme === 'light' || theme === 'sepia';
  return {
    accent,
    fill: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.12)`,
    grid: lightGrid ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)',
    tick: read('--muted', '#8b93a7'),
  };
}

export function ValueChart() {
  const q = useQuery<Series>({ queryKey: ['value-history'], queryFn: () => apiGet<Series>('/api/v1/value-history') });
  const [range, setRange] = useState('1826');
  const [benchOn, setBenchOn] = useState(false);
  const [bench, setBench] = useState<SeriesPoint[] | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const chart = useRef<{ destroy: () => void } | null>(null);

  const points = q.data?.series ?? [];
  const hasHistory = points.length > 0;

  // Draw (or redraw) whenever the range, the overlay or the data changes.
  useEffect(() => {
    if (!hasHistory) return;
    let cancelled = false;
    (async () => {
      const Chart = (await loadChart()) as (new (el: HTMLCanvasElement, cfg: unknown) => { destroy: () => void }) | null;
      if (!Chart || cancelled || !canvas.current) return;
      const span = range === 'all' ? 'all' : Number(range);
      // The range window is `public/pf-range.js`, the same file the EJS page loads: a DAY boundary, not an
      // instant. Comparing instants drops the point ON the cutoff day (the 5Y view then started 2021-11-01
      // here against 2021-10-31 on the EJS page) — see the file's header for the whole story.
      const shown = window.PFRange.within(points, span);
      const colors = chartColors();
      const benchSeries = benchOn ? bench ?? [] : [];
      const labels = shown.map((p) => p.date);

      // The dataset options are the EJS page's, number for number. `tension: 0.15` is not decoration: without
      // it Chart draws the same 1,827 points as straight chords, which puts ~18% more ink on the canvas and
      // reads as a bolder line at the same 2px stroke (measured 11,698 accent pixels against 9,909).
      let datasets;
      if (benchSeries.length > 0) {
        // Percent growth, not dollars: an index near 100–200 against a millions axis would be a flat line at
        // the bottom. Both series are indexed to 0% at the first shown date, and the benchmark is forward-filled
        // so a non-trading day carries the previous close.
        const byDate = new Map(benchSeries.map((p) => [p.date, p.value]));
        const benchAt = (d: string) => {
          let v = byDate.get(d);
          if (v === undefined) {
            for (let i = benchSeries.length - 1; i >= 0; i--) {
              if (benchSeries[i].date <= d) {
                v = benchSeries[i].value;
                break;
              }
            }
          }
          return v;
        };
        const first = shown.find((p) => p.value != null);
        const baseVal = first ? first.value : null;
        let baseBench = first ? benchAt(first.date) : null;
        if (baseBench == null && benchSeries.length) baseBench = benchSeries[0].value;
        const pct = (v: number | null | undefined, base: number | null | undefined) =>
          v == null || base == null || base === 0 ? null : Math.round((v / base - 1) * 10000) / 100;
        datasets = [
          {
            label: 'Portfolio',
            data: shown.map((p) => pct(p.value, baseVal)),
            borderColor: colors.accent,
            backgroundColor: colors.fill,
            fill: true,
            tension: 0.15,
            pointRadius: 0,
            pointHitRadius: 12,
            borderWidth: 2,
          },
          {
            label: 'S&P 500',
            data: shown.map((p) => pct(benchAt(p.date), baseBench)),
            borderColor: colors.tick,
            borderDash: [5, 4],
            fill: false,
            tension: 0.15,
            pointRadius: 0,
            pointHitRadius: 8,
            borderWidth: 1.5,
          },
        ];
      } else {
        datasets = [
          {
            label: 'Total value',
            data: shown.map((p) => p.value),
            borderColor: colors.accent,
            backgroundColor: colors.fill,
            fill: true,
            tension: 0.15,
            pointRadius: 0,
            pointHitRadius: 12,
            borderWidth: 2,
          },
        ];
      }

      if (chart.current) chart.current.destroy();
      chart.current = new Chart(canvas.current, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', axis: 'x', intersect: false },
          plugins: {
            legend: { display: benchSeries.length > 0, labels: { color: colors.tick, boxWidth: 16, boxHeight: 2, font: { size: 10 } } },
            tooltip: {
              callbacks: {
                label: (c: { dataset: { label?: string }; parsed: { y: number } }) =>
                  benchSeries.length > 0
                    ? (c.dataset.label || '') + ': ' + (c.parsed.y >= 0 ? '+' : '') + c.parsed.y.toFixed(1) + '%'
                    : c.parsed.y.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
              },
            },
          },
          scales: {
            x: { ticks: { color: colors.tick, maxTicksLimit: 8, font: { size: 10 } }, grid: { color: colors.grid } },
            y: {
              ticks: {
                color: colors.tick,
                font: { size: 10 },
                callback: (v: number) =>
                  benchSeries.length > 0
                    ? v + '%'
                    : v >= 1e6
                      ? '$' + (v / 1e6).toFixed(1) + 'M'
                      : '$' + (v / 1e3).toFixed(0) + 'k',
              },
              grid: { color: colors.grid },
            },
          },
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [range, benchOn, bench, hasHistory, points]);

  useEffect(() => () => chart.current?.destroy(), []);

  // The overlay is fetched lazily on the first toggle, as the EJS page does, and the series is held in state so
  // the draw effect owns the datasets. (It used to be pushed into the live chart from here, reading a
  // `bench.closes` field the endpoint does not return — so the button went active and nothing was drawn.)
  const toggleBench = async () => {
    const on = !benchOn;
    setBenchOn(on);
    if (!on || bench) return;
    try {
      const raw = (await fetch('/api/v1/benchmark', { credentials: 'same-origin' }).then((r) => r.json())) as
        | { data?: { series?: SeriesPoint[] } }
        | { series?: SeriesPoint[] };
      const body = (raw as { data?: { series?: SeriesPoint[] } }).data ?? (raw as { series?: SeriesPoint[] });
      setBench(body.series ?? []);
    } catch {
      /* the overlay is an extra; failing to fetch it leaves the portfolio line alone */
    }
  };

  // No history, no chart — the EJS card hides itself for a brand-new account.
  if (q.isSuccess && !hasHistory) return null;

  return (
    <div id="value-chart-card" className="panel-card" style={{ margin: '0 0 18px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <h2 className="t-h3" style={{ margin: 0 }}>
          Total value over time
        </h2>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            id="chart-bench-btn"
            className={benchOn ? 'ghost chart-range-active' : 'ghost'}
            title="Overlay the S&P 500 — the chart switches to percent growth from the first date shown (portfolio and S&P both start at 0%)"
            onClick={toggleBench}
          >
            vs SPY
          </button>
          <div id="chart-ranges" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {RANGES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-range={value}
                className={range === value ? 'ghost chart-range-active' : 'ghost'}
                onClick={() => setRange(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ position: 'relative', height: 260 }}>
        <canvas id="value-chart" ref={canvas} />
      </div>
    </div>
  );
}
